import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

const ALL_ROLES = ['SUPERADMIN', 'ADMIN', 'VIEWER'];

/** Markë DIO (profil / live): unazë 3-ngjyrëshe, blu si logo; gri kur tab jo aktiv */
function DioLiveTabIcon({ active }) {
  return (
    <svg
      className={`h-5 w-5 shrink-0 transition-[opacity,filter] duration-200 ${active ? 'opacity-100' : 'opacity-[0.55] grayscale'
        }`}
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        className="fill-blue-300 dark:fill-sky-300"
        d="M5.57 19.66L9.11 15.45A4.5 4.5 0 0 0 9.11 8.55L5.57 4.34A10 10 0 0 1 5.57 19.66Z"
      />
      <path
        className="fill-blue-800 dark:fill-blue-500"
        d="M17 3.34L14.25 8.1A4.5 4.5 0 0 0 15.45 14.89L19.66 18.43A10 10 0 0 1 17 3.34Z"
      />
      <path
        className="fill-blue-600 dark:fill-blue-400"
        d="M19.66 18.43L15.45 14.89A4.5 4.5 0 0 0 9.11 15.45L5.57 19.66A10 10 0 0 1 19.66 18.43Z"
      />
    </svg>
  );
}

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState('dark');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('dio-theme') || 'dark';
    setTheme(savedTheme);
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Si cloud Layout: F8 = temë, F6 = gjuhë – pa butona në header
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'F8') {
        e.preventDefault();
        setTheme((prev) => {
          const next = prev === 'dark' ? 'light' : 'dark';
          localStorage.setItem('dio-theme', next);
          document.documentElement.classList.toggle('dark', next === 'dark');
          return next;
        });
      }
      if (e.key === 'F6') {
        e.preventDefault();
        setLanguage(language === 'sq' ? 'en' : 'sq');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setLanguage, language]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [userMenuOpen]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const allowedPaths =
    user?.allowed_routes && Array.isArray(user.allowed_routes) && user.allowed_routes.length > 0
      ? user.allowed_routes
      : null;

  const passesRoleAndRoutes = useCallback(
    (path, roles) => {
      if (!user?.role || !roles.includes(user.role)) return false;
      if (allowedPaths !== null && !allowedPaths.includes(path)) return false;
      return true;
    },
    [user?.role, allowedPaths],
  );

  const mobileBottomTabs = useMemo(
    () => [
      {
        path: '/dashboard',
        labelKey: 'home',
        roles: ALL_ROLES,
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
        ),
      },
      {
        path: '/devices',
        labelKey: 'devices',
        roles: ALL_ROLES,
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
            />
          </svg>
        ),
      },
      {
        path: '/kosova',
        labelKey: 'fleet_overview',
        roles: ALL_ROLES,
        icon: null,
      },
    ],
    [],
  );

  const visibleBottomTabs = useMemo(() => {
    return mobileBottomTabs.filter((item) => passesRoleAndRoutes(item.path, item.roles));
  }, [mobileBottomTabs, passesRoleAndRoutes]);

  const uneIcon = (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      <div className="hidden md:block">
        <Sidebar />
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header identik me cloud/components/Layout.js */}
        <header className="z-10 bg-white shadow-sm dark:bg-gray-800">
          <div className="flex items-center justify-between px-4 py-2 md:px-4 md:py-3">
            <div className="flex items-center space-x-3">
              <img
                src={`${process.env.PUBLIC_URL || ''}/assets/images/logo-dio-iot.png`}
                alt="DIO IOT"
                className="h-7 object-contain dark:hidden md:h-8"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `${process.env.PUBLIC_URL || ''}/assets/images/logo-full.svg`;
                }}
              />
              <img
                src={`${process.env.PUBLIC_URL || ''}/assets/images/logo-dio-iot-white.png`}
                alt="DIO IOT"
                className="hidden h-7 object-contain dark:block md:h-8"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `${process.env.PUBLIC_URL || ''}/assets/images/logo-white-full.svg`;
                }}
              />
            </div>

            <div className="flex items-center space-x-2 md:space-x-3" ref={userMenuRef}>
              <div className="relative hidden md:block">
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((o) => !o)}
                  className="flex flex-col items-end rounded-lg px-2 py-1 text-right transition hover:bg-gray-100 dark:hover:bg-gray-700"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <p className="text-xs font-semibold text-gray-900 dark:text-white">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{user?.role}</p>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full z-50 mt-1 min-w-[120px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-700">
                    <button
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full rounded px-3 py-2 text-left text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    >
                      {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-16 md:pb-0">{children}</main>

        {/* Bottom nav: faqja e plotë Unë → /me (jo sheet) */}
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 md:hidden safe-area-bottom">
          <div className="flex h-14 items-center justify-around">
            {visibleBottomTabs.map((item) => {
              const tabActive = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex h-full flex-1 flex-col items-center justify-center transition-all duration-200 ease-out hover:-translate-y-0.5 active:translate-y-0 active:scale-95 ${tabActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                    }`}
                >
                  {item.path === '/kosova' ? (
                    <DioLiveTabIcon active={tabActive} />
                  ) : (
                    item.icon
                  )}
                  <span className="mt-0.5 max-w-full truncate px-0.5 text-[10px] font-medium">{t(item.labelKey)}</span>
                </Link>
              );
            })}

            <Link
              to="/me"
              className={`flex h-full flex-1 flex-col items-center justify-center transition-all duration-200 ease-out hover:-translate-y-0.5 active:translate-y-0 active:scale-95 ${isActive('/me') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                }`}
            >
              {uneIcon}
              <span className="mt-0.5 text-[10px] font-medium">{t('une')}</span>
            </Link>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Layout;
